import {Action} from "redux-actions";
import {OnFailure, OnFailureData, OnSuccess, OnSuccessData} from "../actionHelpers";
import {api} from "../../../api/api";
import {Dispatch} from "react-redux";
import Asset from "../../../model/asset/Asset";
import {ASSET_CREATED, ASSET_FETCHED, ASSET_UPDATED, ASSETS_SEARCH_FETCHED} from "./actionType";
import {none, Option, option} from "ts-option";
import Attachment from "../../../model/attachment/Attachment";
import {ImportResult} from "../../../model/importResult/ImportResult";
import AssetSearch from "../../../model/asset/AssetSearch";

/**
 * Asset fetched successful action.
 * @param {Asset} asset from server.
 * @returns {Action<ASSET_FETCHED>}
 */
function assetFetched(asset: Asset): Action<ASSET_FETCHED> {
	return {type: ASSET_FETCHED, payload: {asset}};
}

/**
 * Asset updated successful action.
 * @param {Asset} asset
 * @returns {Action<ASSET_UPDATED>}
 */
function assetUpdated(asset: Asset): Action<ASSET_UPDATED> {
	return {type: ASSET_UPDATED, payload: {asset}};
}

/**
 * Asset created successful action.
 * @param {Asset} asset
 * @returns {Action<ASSET_CREATED>}
 */
function assetCreated(asset: Asset): Action<ASSET_CREATED> {
	return {type: ASSET_CREATED, payload: {asset}};
}

/**
 * Asset search fetched successful action.
 * @param {AssetSearch} asset
 * @returns {Action<ASSET_SEARCH_FETCHED>}
 */
function assetsSearchFetched(assets: AssetSearch[]): Action<ASSETS_SEARCH_FETCHED> {
	return {type: ASSETS_SEARCH_FETCHED, payload: {assets}};
}

/**
 * Fetch assets from server.
 * @param assetId
 * @param {OnSuccess} onSuccess
 * @param {OnFailure} onFailure
 * @return {(dispatch: Dispatch<any>) => void}
 */
export function fetchAsset(assetId: string, onSuccess: OnSuccessData<Asset>, onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.assetApi.fetchAsset(assetId).then(asset => {
			dispatch(assetFetched(asset));
			onSuccess(asset);
		}).catch(onFailure);
	};
}

/**
 * Update asset
 * @param {Asset} asset
 * @param {OnSuccess} onSuccess
 * @param {OnFailure} onFailure
 * @returns {(dispatch: <any>) => void}
 */
export function updateAsset(asset: Asset, onSuccess: OnSuccess, onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.assetApi.updateAsset(asset.id, asset.toJson()).then(() => {
			dispatch(assetUpdated(asset));
			onSuccess();
		}).catch(onFailure);
	};
}

/**
 * Add attachments to asset
 * @param {Asset} asset
 * @param {Attachment[]} file attachments
 * @param {OnSuccess} onSuccess
 * @param {OnFailure} onFailure
 * @returns {(dispatch: <any>) => void}
 */
export function addOrUpdateAssetAttachments(asset: Asset, attachments: Attachment[], onSuccess: OnSuccess, onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.assetApi.addOrUpdateAttachmentIds(asset.id, attachments.map(a => a.id)).then(() => {
			dispatch(assetUpdated(asset.copy({attachments})));
			onSuccess();
		}).catch(() => onFailure("Failure on file attaching to asset"));
	};
}

/**
 * Create asset
 * @param {Asset} asset
 * @param {OnSuccess} onSuccess
 * @param {OnFailure} onFailure
 * @returns {(dispatch: <any>) => void}
 */
export function createAsset(asset: Asset, onSuccess: OnSuccessData<Asset>, onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.assetApi.createAsset(asset.toJson()).then((asset) => {
			dispatch(assetCreated(asset));
			onSuccess(asset);
		}).catch(onFailure);
	};
}

export function uploadAssets(file: File,
	assetAttributeHeader: string,
	onSuccess: OnSuccess,
	onFailure: OnFailureData<Option<ImportResult>>) {
	return function() {
		api.instance.assetApi.uploadAssets(file, assetAttributeHeader).then(onSuccess)
			.catch(error => {
				if (error.response) {
					onFailure(option(ImportResult.fromJson(error.response.data)))
				}
				else {
					onFailure(none);
				}
			});
	};
}

/**
 * Store assets in redux
 * @param asset
 */
export function saveAssetSearch(asset: AssetSearch) {
	return function(dispatch: Dispatch<any>) {
		dispatch(assetsSearchFetched([asset]))
	}
}

/**
 * Fetch assets by ids
 * @param assetIds
 * @param onSuccess
 * @param onFailure
 */
export function fetchAssetsByIds(assetIds: string[], onSuccess: OnSuccess, onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.assetApi.fetchAssetsByIds(assetIds).then((assets: AssetSearch[]) => {
			dispatch(assetsSearchFetched(assets));
			onSuccess();
		}).catch(onFailure);
	};
}